import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setOrgId, getOrgId } from "@/lib/api";
import { setFormatConfig } from "@/lib/format";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyOrgs = useCallback((organizations) => {
    setOrgs(organizations);
    const stored = getOrgId();
    let active = organizations.find((o) => o.id === stored) || organizations[0] || null;
    if (active) {
      setOrgId(active.id);
      setCurrentOrg(active);
      setFormatConfig(active);
    } else {
      setOrgId(null);
      setCurrentOrg(null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    applyOrgs(data.organizations || []);
    return data;
  }, [applyOrgs]);

  const checkAuth = useCallback(async () => {
    try {
      await loadMe();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [loadMe]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const switchOrg = useCallback((org) => {
    setOrgId(org.id);
    setCurrentOrg(org);
  }, []);

  const refreshOrgs = useCallback(async () => {
    const { data } = await api.get("/organizations");
    applyOrgs(data);
    return data;
  }, [applyOrgs]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed, clearing local session anyway", e);
    }
    setUser(false);
    setOrgs([]);
    setCurrentOrg(null);
    setOrgId(null);
  }, []);

  const value = {
    user,
    setUser,
    orgs,
    currentOrg,
    switchOrg,
    refreshOrgs,
    loadMe,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
